import { useEffect, useState, useCallback } from 'react';
import { apiRequest } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';

type ExtraParamType = 'string' | 'integer' | 'boolean';

/**
 * The AI vendor a model is called on. The backend implements one generation flow
 * per value (Vector Engine → Vertex AI for Gemini, Vector Engine → OpenAI for
 * GPT Image) and holds the connection details, so the list is fixed here.
 */
type ModelProvider = 'google-gemini' | 'openai';

const PROVIDER_OPTIONS: { value: ModelProvider; label: string; flow: string }[] = [
  {
    value: 'google-gemini',
    label: 'Google Gemini',
    flow: 'Vector Engine (generateContent) ×2 → Vertex AI',
  },
  {
    value: 'openai',
    label: 'OpenAI',
    flow: 'Vector Engine (/v1/images/edits) ×2 → api.openai.com',
  },
];

/**
 * Entries created before the provider field existed have none stored. They are
 * shown as unset rather than as the provider they would fall back to, so a row
 * that still needs one is visible instead of masked — it routes to Gemini, which
 * fails for an OpenAI model.
 */
const providerLabel = (provider?: ModelProvider): string =>
  PROVIDER_OPTIONS.find((option) => option.value === provider)?.label || 'Not set';

/** Amber for unset, so the rows the migration hasn't reached stand out */
const providerColor = (provider?: ModelProvider): string => {
  if (provider === 'openai') return 'var(--color-success)';
  if (provider === 'google-gemini') return '#79c0ff';
  return 'var(--color-warning-text)';
};

/** Extra provider parameter as stored on the model (value already typed) */
interface ExtraParam {
  key: string;
  value: string | number | boolean;
  type: ExtraParamType;
}

/** Same pair while being edited — the value stays a string until save */
interface ExtraParamRow {
  key: string;
  value: string;
  type: ExtraParamType;
}

interface ImageModel {
  _id: string;
  model_id: string;
  model_name: string;
  /** Which vendor — and therefore which generation flow — this model runs on */
  provider?: ModelProvider;
  credit: string;
  /** Single display name, shown to users in every language */
  display_name?: string;
  aspect_ratio: string[];
  extra_params?: ExtraParam[];
  fallback_enabled?: boolean;
  enabled: boolean;
  order: number;
  /** This model is pre-selected in the app's rebuild picker, and is what a rebuild
   *  with no model named resolves to. At most one model has it. */
  default_for_rebuild?: boolean;
  /** Same, for blend — the two modes are set independently. */
  default_for_blend?: boolean;
}

/** The transform modes whose default model is set per row below. Rebuild and blend
 *  are independent: one model may be the default for both, or for neither — a mode
 *  with no default falls back to the first enabled model in this list's order. */
const MODE_DEFAULTS = [
  { field: 'default_for_rebuild', label: 'Rebuild' },
  { field: 'default_for_blend', label: 'Blend' },
] as const;

type ModeDefaultField = typeof MODE_DEFAULTS[number]['field'];

const COMMON_ASPECT_RATIOS = ['original', '1:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9'];

const EXTRA_PARAM_TYPES: ExtraParamType[] = ['string', 'integer', 'boolean'];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  fontSize: '14px',
  backgroundColor: 'var(--color-input)',
  color: 'var(--color-text)',
  boxSizing: 'border-box',
};

/**
 * The per-mode default pills for one row — one per transform mode, lit when this
 * model is that mode's default.
 *
 * Shared by the mobile card list and the desktop table so the two cannot drift.
 * A disabled model is inert: it can hold no default, because the app's picker never
 * offers it.
 */
function ModeDefaultPills({ model, onToggle }: {
  model: ImageModel;
  onToggle: (model: ImageModel, field: ModeDefaultField) => void;
}) {
  const isDisabled = model.enabled === false;

  return (
    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
      {MODE_DEFAULTS.map(({ field, label }) => {
        const isDefault = model[field] === true;
        return (
          <button
            key={field}
            onClick={() => !isDisabled && onToggle(model, field)}
            disabled={isDisabled}
            title={isDisabled
              ? 'Enable this model before making it a default'
              : isDefault
                ? `Pre-selected for ${label.toLowerCase()} — click to clear, and ${label.toLowerCase()} falls back to the first enabled model`
                : `Make this the default model for ${label.toLowerCase()}`}
            style={{
              padding: '4px 10px',
              borderRadius: '12px',
              border: isDefault ? 'none' : '1px solid var(--color-border)',
              cursor: isDisabled ? 'default' : 'pointer',
              fontSize: '12px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              backgroundColor: isDefault ? 'var(--color-accent-strong)' : 'transparent',
              color: isDisabled ? 'var(--color-border-strong)' : isDefault ? '#ffffff' : 'var(--color-muted)',
            }}
          >
            {isDefault ? `★ ${label}` : label}
          </button>
        );
      })}
    </div>
  );
}

export default function ModelsPage() {
  const [models, setModels] = useState<ImageModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingModel, setEditingModel] = useState<ImageModel | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Form state
  const [formModelName, setFormModelName] = useState('');
  const [formModelId, setFormModelId] = useState('');
  // '' until the admin picks one — nothing is pre-selected on their behalf, so a
  // legacy entry can't be saved as the wrong provider by opening and saving it
  const [formProvider, setFormProvider] = useState<ModelProvider | ''>('');
  const [formCredit, setFormCredit] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formAspectRatios, setFormAspectRatios] = useState<string[]>([]);
  const [formExtraParams, setFormExtraParams] = useState<ExtraParamRow[]>([]);
  const [formFallbackEnabled, setFormFallbackEnabled] = useState(true);
  const [formEnabled, setFormEnabled] = useState(true);

  const fetchModels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest<{
        success: boolean;
        data: ImageModel[];
        error?: string;
      }>('/api/admin/image-models');

      if (Array.isArray(response)) {
        setModels(response);
      } else if (response.success && Array.isArray(response.data)) {
        setModels(response.data);
      } else {
        setError((response as any).error || 'Failed to fetch models');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch models');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const resetForm = () => {
    setFormModelId('');
    setFormModelName('');
    setFormProvider('');
    setFormCredit('');
    setFormDisplayName('');
    setFormAspectRatios([]);
    setFormExtraParams([]);
    setFormFallbackEnabled(true);
    setFormEnabled(true);
  };

  // Escape backs out of the form and the delete confirmation alike.
  useEscapeToClose(showModal, () => { setShowModal(false); setEditingModel(null); resetForm(); });
  useEscapeToClose(!!deleteConfirm, () => setDeleteConfirm(null));

  const handleAddNew = () => {
    setEditingModel(null);
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (model: ImageModel) => {
    setEditingModel(model);
    setFormModelId(model.model_id);
    setFormModelName(model.model_name);
    setFormProvider(model.provider || '');
    setFormCredit(model.credit);
    setFormDisplayName(model.display_name || '');
    setFormAspectRatios(model.aspect_ratio || []);
    setFormExtraParams(
      (model.extra_params || []).map((param) => ({
        key: param.key,
        type: param.type,
        value: String(param.value),
      }))
    );
    setFormFallbackEnabled(model.fallback_enabled !== false);
    setFormEnabled(model.enabled !== false);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await apiRequest<{ success: boolean }>(
        `/api/admin/image-models/${id}`,
        { method: 'DELETE' }
      );
      fetchModels();
      setDeleteConfirm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete model');
    }
  };

  const handleToggleEnabled = async (model: ImageModel) => {
    try {
      await apiRequest<{ success: boolean }>(
        `/api/admin/image-models/${model._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled: !model.enabled })
        }
      );
      fetchModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle model status');
    }
  };

  const handleToggleFallback = async (model: ImageModel) => {
    try {
      await apiRequest<{ success: boolean }>(
        `/api/admin/image-models/${model._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          // undefined counts as on, so flipping means "off unless it was already off"
          body: JSON.stringify({ fallback_enabled: model.fallback_enabled === false })
        }
      );
      fetchModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle fallback');
    }
  };

  /**
   * Make this model a mode's default, or clear it.
   *
   * Radio-like across rows but individually clearable: the backend clears the flag
   * on every other model before setting it here, and a mode left with no default
   * falls back to the first enabled model in list order. Only enabled models can
   * hold a default — the backend rejects the rest.
   */
  const handleToggleModeDefault = async (model: ImageModel, field: ModeDefaultField) => {
    try {
      await apiRequest<{ success: boolean }>(
        `/api/admin/image-models/${model._id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ [field]: !model[field] })
        }
      );
      fetchModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set default model');
    }
  };

  const handleToggleAspectRatio = (ratio: string) => {
    setFormAspectRatios(prev =>
      prev.includes(ratio) ? prev.filter(r => r !== ratio) : [...prev, ratio]
    );
  };

  const handleAddExtraParam = () => {
    setFormExtraParams(prev => [...prev, { key: '', value: '', type: 'string' }]);
  };

  const handleExtraParamChange = (index: number, patch: Partial<ExtraParamRow>) => {
    setFormExtraParams(prev =>
      prev.map((param, i) => {
        if (i !== index) return param;
        const next = { ...param, ...patch };
        // Switching to boolean: default the value to a valid one
        if (patch.type === 'boolean' && next.value !== 'true' && next.value !== 'false') {
          next.value = 'true';
        }
        return next;
      })
    );
  };

  const handleRemoveExtraParam = (index: number) => {
    setFormExtraParams(prev => prev.filter((_, i) => i !== index));
  };

  /**
   * Convert the edited rows to typed values. Rows with an empty key are
   * dropped; an invalid value returns an error message instead.
   */
  const buildExtraParams = (): { params: ExtraParam[] } | { error: string } => {
    const params: ExtraParam[] = [];
    const seen = new Set<string>();

    for (const row of formExtraParams) {
      const key = row.key.trim();
      if (!key) continue;

      if (seen.has(key.toLowerCase())) {
        return { error: `Duplicate parameter key "${key}"` };
      }
      seen.add(key.toLowerCase());

      if (row.type === 'integer') {
        if (row.value.trim() === '' || !Number.isInteger(Number(row.value.trim()))) {
          return { error: `Parameter "${key}" needs an integer value` };
        }
        params.push({ key, type: row.type, value: Number(row.value.trim()) });
      } else if (row.type === 'boolean') {
        if (row.value !== 'true' && row.value !== 'false') {
          return { error: `Parameter "${key}" needs a boolean value` };
        }
        params.push({ key, type: row.type, value: row.value === 'true' });
      } else {
        params.push({ key, type: row.type, value: row.value });
      }
    }

    return { params };
  };

  const handleSaveModel = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const extraParamsResult = buildExtraParams();
    if ('error' in extraParamsResult) {
      setError(extraParamsResult.error);
      return;
    }

    // The select is `required`, so this only catches a bypassed form — never save
    // a model without the provider that decides how it generates
    if (!formProvider) {
      setError('Pick a provider for this model');
      return;
    }

    const modelData = {
      model_id: formModelId.trim(),
      model_name: formModelName.trim(),
      provider: formProvider,
      credit: formCredit,
      display_name: formDisplayName.trim(),
      aspect_ratio: formAspectRatios,
      extra_params: extraParamsResult.params,
      fallback_enabled: formFallbackEnabled,
      enabled: formEnabled,
    };

    try {
      const url = editingModel
        ? `/api/admin/image-models/${editingModel._id}`
        : '/api/admin/image-models';

      await apiRequest<{ success: boolean }>(
        url,
        {
          method: editingModel ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(modelData)
        }
      );

      fetchModels();
      setShowModal(false);
      setEditingModel(null);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save model');
    }
  };

  // Drag-and-drop reorder handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newModels = [...models];
    const draggedItem = newModels[draggedIndex];
    newModels.splice(draggedIndex, 1);
    newModels.splice(index, 0, draggedItem);

    setModels(newModels);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;

    const reorderedModels = models.map((m, index) => ({
      id: m._id,
      order: index,
    }));

    try {
      await apiRequest<{ success: boolean }>(
        '/api/admin/image-models/reorder',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ models: reorderedModels })
        }
      );
      fetchModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder models');
      fetchModels();
    }

    setDraggedIndex(null);
  };

  // Arrow-based reorder (mobile, where HTML5 drag is unreliable)
  const moveModel = async (index: number, dir: -1 | 1) => {
    const ni = index + dir;
    if (ni < 0 || ni >= models.length) return;
    const reordered = [...models];
    [reordered[index], reordered[ni]] = [reordered[ni], reordered[index]];
    setModels(reordered);
    try {
      await apiRequest<{ success: boolean }>(
        '/api/admin/image-models/reorder',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ models: reordered.map((m, i) => ({ id: m._id, order: i })) })
        }
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder models');
      fetchModels();
    }
  };

  if (loading && models.length === 0) {
    return (
      <div className="page-status">Loading models...</div>
    );
  }

  return (
    <div className="page">
      <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>AI Models</h1>
        <button
          onClick={handleAddNew}
          style={{
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          + Add Model
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          backgroundColor: 'var(--color-danger-surface)',
          color: 'var(--color-danger)',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      <div style={{ marginBottom: '16px', color: 'var(--color-muted)', fontSize: '14px' }}>
        {models.length} model{models.length !== 1 ? 's' : ''} • Drag rows to reorder
        <div style={{ marginTop: '4px', fontSize: '13px', color: 'var(--color-subtle)' }}>
          Rebuild and blend each open on their ★ model. A mode with none starred falls back to the
          first enabled model in this order.
        </div>
      </div>

      {isMobile ? (
        models.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)', backgroundColor: 'var(--color-surface)', borderRadius: '8px' }}>
            No models found. Tap "+ Add Model" to create one.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {models.map((model, index) => (
              <div key={model._id} style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: 'var(--color-text)', fontSize: '15px' }}>
                      <span style={{ color: 'var(--color-muted)', marginRight: '6px' }}>#{index + 1}</span>{model.display_name || '-'}
                    </div>
                    <div style={{ color: 'var(--color-muted)', fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all', marginTop: '2px' }}>
                      {model.model_id}{model.model_id !== model.model_name ? ` → ${model.model_name}` : ''}
                    </div>
                    <div style={{ fontSize: '12px', marginTop: '4px', color: providerColor(model.provider) }}>
                      {providerLabel(model.provider)}
                    </div>
                  </div>
                  <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: 'var(--color-accent-strong)', color: '#fff', fontSize: '12px', fontWeight: 500, flexShrink: 0 }}>{model.credit}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {(model.aspect_ratio || []).map((ratio) => (
                    <span key={ratio} style={{ padding: '2px 6px', borderRadius: '3px', backgroundColor: 'var(--color-border)', color: 'var(--color-muted)', fontSize: '11px' }}>{ratio}</span>
                  ))}
                  {(!model.aspect_ratio || model.aspect_ratio.length === 0) && <span style={{ color: 'var(--color-subtle)', fontSize: '12px' }}>No aspect ratios</span>}
                </div>
                {(model.extra_params || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {(model.extra_params || []).map((param) => (
                      <span key={param.key} style={{ padding: '2px 6px', borderRadius: '3px', backgroundColor: '#1f2937', color: 'var(--color-muted)', fontSize: '11px', fontFamily: 'monospace' }}>
                        {param.key}: {String(param.value)}
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderTop: '1px solid var(--color-border)', paddingTop: '10px', flexWrap: 'wrap' }}>
                  <button onClick={() => handleToggleEnabled(model)} style={{ padding: '6px 12px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 500, backgroundColor: model.enabled !== false ? 'var(--color-success-strong)' : 'var(--color-subtle)', color: 'white' }}>
                    {model.enabled !== false ? 'Enabled' : 'Disabled'}
                  </button>
                  <button onClick={() => handleToggleFallback(model)} style={{ padding: '6px 12px', borderRadius: '12px', border: '1px solid var(--color-border)', cursor: 'pointer', fontSize: '12px', fontWeight: 500, backgroundColor: 'transparent', color: model.fallback_enabled !== false ? 'var(--color-success)' : 'var(--color-muted)' }}>
                    3rd fallback: {model.fallback_enabled !== false ? 'On' : 'Off'}
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}>
                    <span style={{ color: 'var(--color-subtle)', fontSize: '12px' }}>Default for</span>
                    <ModeDefaultPills model={model} onToggle={handleToggleModeDefault} />
                  </div>
                  <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                    <button onClick={() => moveModel(index, -1)} disabled={index === 0} style={{ padding: '6px 10px', backgroundColor: 'transparent', color: index === 0 ? 'var(--color-border-strong)' : 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: index === 0 ? 'default' : 'pointer' }}>▲</button>
                    <button onClick={() => moveModel(index, 1)} disabled={index === models.length - 1} style={{ padding: '6px 10px', backgroundColor: 'transparent', color: index === models.length - 1 ? 'var(--color-border-strong)' : 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '4px', cursor: index === models.length - 1 ? 'default' : 'pointer' }}>▼</button>
                  </div>
                  <button onClick={() => handleEdit(model)} style={{ padding: '6px 14px', backgroundColor: 'var(--color-accent-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Edit</button>
                  <button onClick={() => setDeleteConfirm(model._id)} style={{ padding: '6px 14px', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
      <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: 'var(--color-surface-hover)' }}>
            <tr>
              <th style={{ padding: '12px', width: '40px', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}></th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Model ID</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Model Name</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }} title="The vendor called, which selects the generation flow">Provider</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Display Name</th>
              <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Credit</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Aspect Ratios</th>
              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Extra Params</th>
              <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }} title="Allow the 3rd attempt to fall back to Vertex AI / OpenAI">3rd Fallback</th>
              <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Enabled</th>
              <th
                style={{ padding: '12px', textAlign: 'center', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}
                title="The model each mode's picker pre-selects. Set per mode; a mode with none falls back to the first enabled model in this order"
              >
                Default For
              </th>
              <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {models.map((model, index) => (
              <tr
                key={model._id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  borderBottom: '1px solid var(--color-border)',
                  cursor: 'move',
                  backgroundColor: draggedIndex === index ? '#1f6feb20' : 'transparent'
                }}
              >
                <td style={{ padding: '12px', textAlign: 'center', color: 'var(--color-subtle)' }}>
                  ⋮⋮
                </td>
                <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '13px', color: 'var(--color-text)' }}>
                  {model.model_id}
                </td>
                <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '13px', color: 'var(--color-muted)' }} title="Provider model actually called">
                  {model.model_name}
                </td>
                <td style={{ padding: '12px' }}>
                  <span style={{
                    padding: '3px 8px',
                    borderRadius: '12px',
                    border: '1px solid var(--color-border)',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    color: providerColor(model.provider)
                  }}>
                    {providerLabel(model.provider)}
                  </span>
                </td>
                <td style={{ padding: '12px', fontWeight: '500', color: 'var(--color-text)' }}>
                  {model.display_name || '-'}
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: 'var(--color-accent-strong)',
                    color: '#ffffff',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {model.credit}
                  </span>
                </td>
                <td style={{ padding: '12px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {(model.aspect_ratio || []).map((ratio) => (
                      <span key={ratio} style={{
                        padding: '2px 6px',
                        borderRadius: '3px',
                        backgroundColor: 'var(--color-border)',
                        color: 'var(--color-muted)',
                        fontSize: '11px'
                      }}>
                        {ratio}
                      </span>
                    ))}
                    {(!model.aspect_ratio || model.aspect_ratio.length === 0) && (
                      <span style={{ color: 'var(--color-subtle)', fontSize: '12px' }}>-</span>
                    )}
                  </div>
                </td>
                <td style={{ padding: '12px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {(model.extra_params || []).map((param) => (
                      <span key={param.key} style={{
                        padding: '2px 6px',
                        borderRadius: '3px',
                        backgroundColor: 'var(--color-border)',
                        color: 'var(--color-muted)',
                        fontSize: '11px',
                        fontFamily: 'monospace'
                      }}>
                        {param.key}: {String(param.value)}
                      </span>
                    ))}
                    {(!model.extra_params || model.extra_params.length === 0) && (
                      <span style={{ color: 'var(--color-subtle)', fontSize: '12px' }}>-</span>
                    )}
                  </div>
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <button
                    onClick={() => handleToggleFallback(model)}
                    title={model.fallback_enabled !== false
                      ? 'Falls back to Vertex AI / OpenAI on the 3rd attempt'
                      : 'Fails after the two Vector Engine attempts'}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: model.fallback_enabled !== false ? 'var(--color-success-strong)' : 'var(--color-subtle)',
                      color: 'white'
                    }}
                  >
                    {model.fallback_enabled !== false ? 'On' : 'Off'}
                  </button>
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <button
                    onClick={() => handleToggleEnabled(model)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: model.enabled !== false ? 'var(--color-success-strong)' : 'var(--color-subtle)',
                      color: 'white'
                    }}
                  >
                    {model.enabled !== false ? 'Yes' : 'No'}
                  </button>
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <ModeDefaultPills model={model} onToggle={handleToggleModeDefault} />
                </td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <button
                    onClick={() => handleEdit(model)}
                    style={{
                      padding: '4px 12px',
                      marginRight: '8px',
                      backgroundColor: 'var(--color-accent-strong)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(model._id)}
                    style={{
                      padding: '4px 12px',
                      backgroundColor: 'var(--color-danger-strong)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {models.length === 0 && (
              <tr>
                <td colSpan={12} style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>
                  No models found. Click "+ Add Model" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: '8px',
            padding: '24px',
            width: '640px',
            maxWidth: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--color-text)' }}>
              {editingModel ? 'Edit Model' : 'Add New Model'}
            </h2>

            <form onSubmit={handleSaveModel}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: 'var(--color-text)' }}>
                  Model ID *
                </label>
                <input
                  type="text"
                  value={formModelId}
                  onChange={(e) => setFormModelId(e.target.value)}
                  required
                  placeholder="e.g. gpt-image-2-medium"
                  style={{ ...inputStyle, fontFamily: 'monospace' }}
                />
                <div style={{ color: 'var(--color-muted)', fontSize: '12px', marginTop: '4px' }}>
                  Unique identity — the app sends this and lenses store it. To offer one model
                  twice with different parameters, give each entry its own ID
                  (e.g. <code>gpt-image-2-high</code> / <code>gpt-image-2-medium</code>).
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: 'var(--color-text)' }}>
                  Model Name *
                </label>
                <input
                  type="text"
                  value={formModelName}
                  onChange={(e) => setFormModelName(e.target.value)}
                  required
                  placeholder="e.g. gpt-image-2"
                  style={{ ...inputStyle, fontFamily: 'monospace' }}
                />
                <div style={{ color: 'var(--color-muted)', fontSize: '12px', marginTop: '4px' }}>
                  The model id sent to Vector Engine / Vertex AI / OpenAI. Variants of the same
                  model share this value.
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: 'var(--color-text)' }}>
                  Provider *
                </label>
                <select
                  value={formProvider}
                  onChange={(e) => setFormProvider(e.target.value as ModelProvider | '')}
                  required
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="">Select a provider…</option>
                  {PROVIDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <div style={{ color: 'var(--color-muted)', fontSize: '12px', marginTop: '4px' }}>
                  Decides which flow every generation on this model takes:
                  <div style={{ marginTop: '4px' }}>
                    {PROVIDER_OPTIONS.map((option) => (
                      <div key={option.value} style={{ color: option.value === formProvider ? 'var(--color-text)' : 'var(--color-subtle)' }}>
                        {option.label} → <code>{option.flow}</code>
                      </div>
                    ))}
                  </div>
                  {!formProvider && (
                    <div style={{ color: 'var(--color-warning-text)', marginTop: '6px' }}>
                      Not set yet — generation currently routes this model by its name.
                      Pick the provider it actually runs on.
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: 'var(--color-text)' }}>
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formDisplayName}
                  onChange={(e) => setFormDisplayName(e.target.value)}
                  required
                  placeholder="e.g. GPT Image 2.0"
                  style={inputStyle}
                />
                <div style={{ color: 'var(--color-muted)', fontSize: '12px', marginTop: '4px' }}>
                  What users see in the app, in every language.
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: 'var(--color-text)' }}>
                  Credit Cost *
                </label>
                <input
                  type="text"
                  value={formCredit}
                  onChange={(e) => setFormCredit(e.target.value)}
                  required
                  placeholder="e.g. 8"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--color-border)',
                    backgroundColor: 'var(--color-input)',
                    color: 'var(--color-text)',
                    borderRadius: '4px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: 'var(--color-text)' }}>
                  Aspect Ratios
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {COMMON_ASPECT_RATIOS.map((ratio) => (
                    <button
                      key={ratio}
                      type="button"
                      onClick={() => handleToggleAspectRatio(ratio)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid',
                        borderColor: formAspectRatios.includes(ratio) ? 'var(--color-success-strong)' : 'var(--color-border)',
                        backgroundColor: formAspectRatios.includes(ratio) ? 'var(--color-success-strong)' : 'var(--color-input)',
                        color: formAspectRatios.includes(ratio) ? 'white' : 'var(--color-muted)',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      {ratio}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: 'var(--color-text)' }}>
                  Extra API Parameters
                </label>
                <div style={{ color: 'var(--color-muted)', fontSize: '12px', marginBottom: '8px' }}>
                  Sent with every generation request for this model (e.g. <code>quality</code>: <code>low</code>,{' '}
                  <code>format</code>: <code>jpeg</code>). Gemini models accept dot notation for nested config
                  (e.g. <code>imageConfig.imageSize</code>).
                </div>

                {formExtraParams.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
                    {formExtraParams.map((param, index) => (
                      <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                          type="text"
                          value={param.key}
                          onChange={(e) => handleExtraParamChange(index, { key: e.target.value })}
                          placeholder="key"
                          style={{ ...inputStyle, flex: 2, minWidth: '120px', fontFamily: 'monospace' }}
                        />
                        <select
                          value={param.type}
                          onChange={(e) => handleExtraParamChange(index, { type: e.target.value as ExtraParamType })}
                          style={{ ...inputStyle, flex: 1, minWidth: '96px', cursor: 'pointer' }}
                        >
                          {EXTRA_PARAM_TYPES.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                        {param.type === 'boolean' ? (
                          <select
                            value={param.value}
                            onChange={(e) => handleExtraParamChange(index, { value: e.target.value })}
                            style={{ ...inputStyle, flex: 2, minWidth: '120px', cursor: 'pointer' }}
                          >
                            <option value="true">true</option>
                            <option value="false">false</option>
                          </select>
                        ) : (
                          <input
                            type={param.type === 'integer' ? 'number' : 'text'}
                            step={param.type === 'integer' ? 1 : undefined}
                            value={param.value}
                            onChange={(e) => handleExtraParamChange(index, { value: e.target.value })}
                            placeholder="value"
                            style={{ ...inputStyle, flex: 2, minWidth: '120px', fontFamily: 'monospace' }}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveExtraParam(index)}
                          title="Remove parameter"
                          style={{
                            padding: '8px 10px',
                            backgroundColor: 'transparent',
                            color: 'var(--color-danger)',
                            border: '1px solid var(--color-border)',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            flexShrink: 0
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleAddExtraParam}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: 'transparent',
                    color: 'var(--color-accent)',
                    border: '1px dashed var(--color-border)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  + Add Parameter
                </button>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500', color: 'var(--color-text)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formFallbackEnabled}
                    onChange={(e) => setFormFallbackEnabled(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  Allow 3rd attempt fallback
                </label>
                <div style={{ color: 'var(--color-muted)', fontSize: '12px', marginTop: '4px', marginLeft: '24px' }}>
                  On: after the two Vector Engine attempts fail, retry once directly on the
                  provider selected above (Vertex AI for Google Gemini, api.openai.com for
                  OpenAI). Off: the generation fails after the two Vector Engine attempts.
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '500', color: 'var(--color-text)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formEnabled}
                    onChange={(e) => setFormEnabled(e.target.checked)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  Enabled (visible to mobile users)
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingModel(null);
                    resetForm();
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--color-subtle)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--color-success-strong)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {editingModel ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: '8px',
            padding: '24px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: 'var(--color-text)' }}>Confirm Delete</h3>
            <p style={{ marginBottom: '20px', color: 'var(--color-muted)' }}>
              Are you sure you want to delete this model? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--color-subtle)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--color-danger-strong)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
